import Footer from "../Footer/Footer";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useI18n } from "../../../i18n/I18nContext";

const guarantees = [
  {
    years: "2",
    label: "anos de garantia",
    title: "Carga Pesada",
    description:
      "Produtos de carga pesada têm uma garantia de 2 anos a contar da data de aquisição.",
    examples: ["Porta-paletes", "Estruturas industriais", "Sistemas de alta densidade"],
  },
  {
    years: "5",
    label: "anos de garantia",
    title: "Carga Média, Mezaninos & Cantilever",
    description:
      "Produtos de carga média, mezaninos e cantilever beneficiam de uma garantia alargada de 5 anos.",
    examples: ["Mezaninos", "Estantes cantilever", "Sistemas de carga média"],
    featured: true,
  },
];

export default function Guarantee() {
  const { t } = useI18n();

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Helmet>
        <title>{t("Garantia dos Produtos | Racksforall")}</title>
        <meta name="description" content={t("Informações sobre a garantia dos nossos racks e estantes metálicas. Comprometemo-nos com a qualidade e durabilidade dos produtos.")} />
        <link rel="canonical" href="https://racksforall.pt/guarantee" />
        <meta property="og:title" content={t("Garantia | Racksforall")} />
        <meta property="og:url" content="https://racksforall.pt/guarantee" />
      </Helmet>
      <main className="flex-1 pt-20">

        {/* Hero */}
        <section className="bg-gray-50 border-b border-black/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">
            <div className="grid lg:grid-cols-12 gap-8 items-end">
              <div className="lg:col-span-7">
                <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-indigo-600 mb-4">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
                  </svg>
                  {t("Garantia")}
                </span>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-gray-950 leading-tight">
                  {t("Confiança garantida")}<br />
                  <span className="text-indigo-700">{t("em cada produto")}</span>
                </h1>
              </div>

              <div className="lg:col-span-5">
                <div className="rounded-2xl border border-black/10 bg-white p-5 sm:p-6 shadow-sm">
                  <p className="text-sm text-gray-600 leading-7">
                    {t("Na Racksforall, os nossos produtos são fabricados segundo processos rigorosos e normas europeias. Cada gama tem condições de garantia adaptadas às suas características técnicas.")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Cards de garantia */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14 sm:py-16">
          <div className="grid sm:grid-cols-2 gap-6">
            {guarantees.map((g) => (
              <div
                key={g.title}
                className={`relative rounded-3xl p-7 sm:p-8 overflow-hidden flex flex-col justify-between min-h-[280px] ${g.featured
                    ? "bg-indigo-700 text-white"
                    : "border border-black/10 bg-white text-gray-900"
                  }`}
              >
                {/* Número de fundo decorativo */}
                <span
                  className={`pointer-events-none absolute -right-4 -bottom-6 text-[120px] font-black leading-none select-none ${g.featured ? "text-white/10" : "text-gray-100"
                    }`}
                >
                  {g.years}
                </span>

                <div className="relative">
                  <div className="flex items-end gap-2 mb-4">
                    <span
                      className={`text-6xl font-black tracking-tight leading-none ${g.featured ? "text-white" : "text-indigo-700"
                        }`}
                    >
                      {g.years}
                    </span>
                    <span
                      className={`text-sm font-semibold mb-2 ${g.featured ? "text-indigo-200" : "text-gray-500"
                        }`}
                    >
                      {t(g.label)}
                    </span>
                  </div>

                  <h2
                    className={`text-xl sm:text-2xl font-bold tracking-tight mb-3 ${g.featured ? "text-white" : "text-gray-950"
                      }`}
                  >
                    {t(g.title)}
                  </h2>

                  <p
                    className={`text-sm leading-7 max-w-sm ${g.featured ? "text-indigo-100" : "text-gray-600"
                      }`}
                  >
                    {t(g.description)}
                  </p>
                </div>

                <div className="relative mt-6">
                  <div
                    className={`h-px w-full mb-4 ${g.featured ? "bg-white/15" : "bg-black/10"
                      }`}
                  />
                  <div className="flex flex-wrap gap-2">
                    {g.examples.map((ex) => (
                      <span
                        key={ex}
                        className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${g.featured
                            ? "bg-white/15 text-indigo-100"
                            : "bg-gray-100 text-gray-600"
                          }`}
                      >
                        {t(ex)}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Secção carga leve */}
        <section className="border-y border-black/10 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-14">
            <div className="grid lg:grid-cols-12 gap-8 items-center">

              <div className="lg:col-span-2 hidden lg:flex items-center justify-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white border border-black/10 shadow-sm">
                  <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.563.563 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" />
                  </svg>
                </div>
              </div>

              <div className="lg:col-span-7">
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400 mb-2 block">
                  {t("Nota sobre carga leve")}
                </span>
                <h2 className="text-xl sm:text-2xl font-black tracking-tight text-gray-950 mb-3">
                  {t("Sem certificação obrigatória, com total rigor de fabrico")}
                </h2>
                <p className="text-gray-600 leading-7 max-w-2xl">
                  {t("Os produtos de carga leve não obrigam à necessidade de uma certificação, porém os mesmos passam pelos processos e procedimentos de fabrico da carga média — garantindo o mesmo nível de qualidade e segurança.")}
                </p>
              </div>

              <div className="lg:col-span-3">
                <div className="rounded-2xl bg-white border border-black/10 p-5 shadow-sm space-y-3">
                  {[
                    "Processo de fabrico rigoroso",
                    "Materiais de qualidade equivalente",
                    "Procedimentos da carga média",
                  ].map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-indigo-50">
                        <svg className="w-3 h-3 text-indigo-600" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                      <p className="text-sm text-gray-700 leading-snug">{t(item)}</p>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14 sm:py-16">
          <div className="rounded-3xl bg-gray-950 text-white px-7 sm:px-10 py-8 sm:py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/50 mb-2">
                {t("Dúvidas sobre garantia?")}
              </p>
              <h3 className="text-xl sm:text-2xl font-black tracking-tight">
                {t("Fale diretamente com a nossa equipa.")}
              </h3>
            </div>
            <div className="flex flex-wrap gap-3 shrink-0">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center rounded-full bg-indigo-600 px-5 py-3 text-sm font-semibold text-white hover:bg-indigo-700 transition-colors"
              >
                {t("Contactar")}
              </Link>
              <Link
                to="/faq"
                className="inline-flex items-center justify-center rounded-full border border-white/15 px-5 py-3 text-sm font-semibold text-white hover:bg-white/5 transition-colors"
              >
                {t("Ver FAQ")}
              </Link>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}